import PropTypes from 'prop-types';
import React from 'react';
import alphabet from 'alphabet';
import { APOSTROPHE, ARIA_LABELS, SPANISH_ALPHABET } from '../constants/text';
import SplitLayout from '../common/layouts/splitLayout';
import AudioContainer from '../common/audio/audioContainer';
import StimulusButton from '../common/stimulus/StimulusButton';
import TextField from '../common/components/TextField';
import PointerCursor from '../common/components/PointerCursor';
import LetterButtonGrid from '../common/components/LetterButtonGrid';
import ScreenErrorContainer from '../common/error/ScreenErrorContainer';
import POSITION from '../constants/pointerCursor';
import GoButton from '../common/components/GoButton';
import { LANGUAGES } from '../common/localization';

export default class SpellingView extends React.Component {
    constructor(props) {
        super(props);
        this.playStimulus = this.playStimulus.bind(this);
    }

    componentDidMount() {
        if (!this.props.isDataFetched) {
            this.props.fetchContent();
        } else {
            this.props.narrateScreen(this.props.stimulus.audio);
        }
        window.addEventListener('popstate', this.props.resetSelectedResponses);
    }

    componentDidUpdate(prevProps) {
        if (this.props.isDataFetched && this.narrationShouldStart(prevProps)) {
            this.props.narrateScreen(this.props.stimulus.audio);
        }
    }

    componentWillUnmount() {
        this.props.cleanup();
        window.removeEventListener('popstate', this.props.resetSelectedResponses);
    }

    getStimulusArea() {
        return (
            <div>
                <StimulusButton
                    isPlaying={this.props.isStimulusPlaying}
                    isDisabled={
                        this.props.isScreenBlocked ||
                        this.props.isGreyedOut ||
                        !this.props.isClearButtonEnabled
                    }
                    onClick={this.playStimulus}
                />
                <TextField
                    onClearButtonClick={this.props.resetSelectedResponses}
                    text={this.props.selectedResponses.join('')}
                    isDisabled={
                        this.props.isScreenBlocked ||
                        this.props.isGreyedOut ||
                        !this.props.isClearButtonEnabled
                    }
                    isVisible={this.props.selectedResponses.length > 0}
                />
            </div>
        );
    }

    getResponsesArea() {
        return (
            <LetterButtonGrid
                buttons={
                    this.props.currentLanguage === LANGUAGES.SPANISH
                        ? SPANISH_ALPHABET
                        : [...alphabet.lower, APOSTROPHE]
                }
                selectedIndex={this.props.selectedIndex}
                enabledButtons={this.props.enabledResponses}
                isEnabled={this.props.isButtonGridEnabled && !this.props.isScreenBlocked}
                onButtonClick={this.props.selectResponse}
                isDisabled={this.props.isGreyedOut}
            />
        );
    }

    playStimulus() {
        if (this.props.stimulus.audio) {
            this.props.narrateStimulus(this.props.stimulus.audio, 0);
        }
    }

    narrationShouldStart(prevProps) {
        const isReady = this.props.isDataFetched;
        const shouldStartNarrate =
            this.props.isDataFetched !== prevProps.isDataFetched ||
            this.props.questionIndex !== prevProps.questionIndex;
        return isReady && shouldStartNarrate;
    }

    render() {
        return (
            <SplitLayout
                class={this.props.pageLayout}
                upperElement={this.getStimulusArea()}
                lowerElement={this.getResponsesArea()}
                isScreenBlocked={this.props.isScreenBlocked}
                isGreyedOut={this.props.isGreyedOut}
            >
                <ScreenErrorContainer onRetry={this.props.onNext} />
                {this.props.isPointerVisible && (
                    <PointerCursor
                        position={this.props.pointerPosition}
                        clicked={this.props.isPointerClicked}
                    />
                )}
                {this.props.isNextButtonVisible && (
                    <GoButton
                        onClick={this.props.onNext}
                        isEnabled={!this.props.isScreenBlocked && this.props.isNextButtonClickable}
                        ariaLabel={ARIA_LABELS.GO_TO_NEXT_STEP}
                    />
                )}
                <AudioContainer />
            </SplitLayout>
        );
    }
}

SpellingView.propTypes = {
    fetchContent: PropTypes.func.isRequired,
    narrateScreen: PropTypes.func.isRequired,
    selectResponse: PropTypes.func.isRequired,
    cleanup: PropTypes.func,
    onNext: PropTypes.func,
    resetSelectedResponses: PropTypes.func,
    narrateStimulus: PropTypes.func,
    pointerPosition: PropTypes.string,
    isPointerClicked: PropTypes.bool,
    questionIndex: PropTypes.number,
    isDataFetched: PropTypes.bool,
    isScreenBlocked: PropTypes.bool,
    isGreyedOut: PropTypes.bool,
    isStimulusPlaying: PropTypes.bool,
    isPointerVisible: PropTypes.bool,
    isNextButtonVisible: PropTypes.bool,
    isNextButtonClickable: PropTypes.bool,
    isButtonGridEnabled: PropTypes.bool,
    isClearButtonEnabled: PropTypes.bool,
    pageLayout: PropTypes.string,
    stimulus: PropTypes.object,
    selectedIndex: PropTypes.string,
    enabledResponses: PropTypes.array,
    selectedResponses: PropTypes.array,
    currentLanguage: PropTypes.string,
};

SpellingView.defaultProps = {
    isDataFetched: false,
    isStimulusPlaying: false,
    isScreenBlocked: false,
    isGreyedOut: false,
    isPointerVisible: true,
    isNextButtonVisible: false,
    isNextButtonClickable: true,
    isButtonGridEnabled: true,
    isClearButtonEnabled: true,
    isPointerClicked: false,
    pointerPosition: POSITION.START_POSITION,
    questionIndex: undefined,
    pageLayout: undefined,
    stimulus: undefined,
    selectedIndex: undefined,
    enabledResponses: [],
    selectedResponses: [],
    resetSelectedResponses: () => {},
    onNext: () => {},
    narrateStimulus: () => {},
    cleanup: () => {},
    currentLanguage: '',
};
